import { useCallback } from 'react';
import cookies from 'browser-cookies';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { z } from 'zod';
import { G } from '@mobily/ts-belt';
import { getWindow } from '@stitch-fix/mode-react';
import { highIntentCookieSchema } from './highIntentCookieSchema';
import {
  DynamicProductAdCreateMutationVariables,
  useDynamicProductAdCreateMutation,
} from '../../dynamicAds/graphql/DynamicProductAdCreateMutation';
import { useGetClientStyleProfileLazyQuery } from '../../dynamicAds/graphql/getClientStyleProfile';
import { CapitalizedBusinessLine } from '../../../components/SocialButtons';
import { getVisitorIdCookie } from '../../../hooks/useAllocation/useAllocation';
import type { DynamicProductAd } from '../../../types/definitions/vendor/graphql-schema';
import { HIGH_INTENT_COOKIE_NAME } from '../../../utils/helpers';
import { browserLogger } from '../../../utils/loggers/browser';

export const eraseHighIntentCookie = () => {
  cookies.erase(HIGH_INTENT_COOKIE_NAME, {
    domain: '.stitchfix.com',
    path: '/',
    secure: true,
  });
};

export const useDynamicProductAd = () => {
  const [mutate] = useDynamicProductAdCreateMutation();
  const [getClientStyleProfile] = useGetClientStyleProfileLazyQuery();

  const dynamicProductAdCreate = useCallback(
    async ({
      clientType,
      variables,
    }: {
      clientType: 'signup' | 'login';
      variables: DynamicProductAdCreateMutationVariables;
    }) => {
      let abortReason;

      try {
        const { data, error } = await getClientStyleProfile({
          fetchPolicy: 'no-cache',
        });
        const businessLine = data?.client?.businessLine;
        const hasCompletedStyleProfileQuiz =
          data?.client?.capabilities?.hasCompletedStyleProfile;
        // Enable existing clients to update their DPA if they had one previously
        const isExistingClientIneligible =
          clientType === 'login' &&
          G.isNullable(data?.client?.dynamicProductAd);

        if (businessLine !== 'WOMENS') abortReason = 'business_line';
        if (hasCompletedStyleProfileQuiz)
          abortReason = 'style_profile_complete';
        if (isExistingClientIneligible)
          abortReason = 'existing_client_without_dpa';
        if (error) abortReason = 'get_client_style_profile_error';

        if (abortReason) {
          recordMark('dynamic_product_ad_create_mutation_aborted', {
            reason: abortReason,
            client_type: clientType,
          });

          if (error) throw error;

          return;
        }

        const mutationResponse = await mutate({
          variables,
        });
        const result = mutationResponse.data?.dynamicProductAdCreate?.result;
        const isSuccessful =
          // eslint-disable-next-line no-underscore-dangle
          result?.__typename === 'DynamicProductAdCreateSuccess';

        recordMark('dynamic_product_ad_create_mutation_completed', {
          graph_mutation_succeeded: isSuccessful,
          client_type: clientType,
        });

        if (!isSuccessful) {
          const message =
            // eslint-disable-next-line no-underscore-dangle
            result?.__typename === 'DynamicProductAdCreateError'
              ? result.message
              : 'Unsuccessful mutation result';

          throw new Error(message || 'Unknown error');
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Unknown error';

        // Don't record a mark if the mutation was aborted
        if (!abortReason) {
          recordMark('dynamic_product_ad_create_mutation_completed', {
            graph_mutation_succeeded: false,
            client_type: clientType,
          });
        }

        browserLogger({
          feature: 'high-intent',
          level: 'error',
          message: `Error creating client dynamic product ad. Reason: ${message}`,
          team: 'client-onboarding',
          context: {
            error: e,
            dynamicProductAdId: variables.input.id,
            grouping: variables.input.grouping,
            clientType,
          },
        });
      }
    },
    [mutate, getClientStyleProfile],
  );

  return dynamicProductAdCreate;
};

export const getHighIntentCookie = () => {
  const win = getWindow();

  if (!win) return undefined;

  const intentCookie = cookies.get(HIGH_INTENT_COOKIE_NAME);

  if (intentCookie) {
    try {
      const decodedCookie = new URLSearchParams(intentCookie);
      const parsedCookie = highIntentCookieSchema.parse({
        grouping: decodedCookie.get('grouping'),
        visitor_id: decodedCookie.get('visitor_id'),
      });

      const currentVisitorId = getVisitorIdCookie();

      if (parsedCookie.visitor_id !== currentVisitorId) {
        browserLogger({
          feature: 'high-intent',
          level: 'warn',
          message: `Invalid ${HIGH_INTENT_COOKIE_NAME} cookie: visitor ID mismatch. Expected ${currentVisitorId}, found ${parsedCookie.visitor_id}`,
          team: 'client-onboarding',
          context: {
            cookieData: parsedCookie,
            visitorId: currentVisitorId,
            referrer: win.document.referrer || '',
          },
        });

        return undefined;
      }

      const cookie = {
        ...parsedCookie,
        id: '',
      };

      return cookie;
    } catch (error: unknown) {
      browserLogger({
        feature: 'high-intent',
        level: 'warn',
        message: `Failed to parse ${HIGH_INTENT_COOKIE_NAME} cookie`,
        team: 'client-onboarding',
        context: {
          error: error instanceof z.ZodError ? error.format() : error,
        },
      });
    }
  }

  return undefined;
};

export const getResolvedHighIntentData = ({
  dynamicProductAd,
  businessLine,
}: {
  dynamicProductAd?: Pick<DynamicProductAd, 'id' | 'grouping' | 'keyword'>;
  businessLine?: CapitalizedBusinessLine;
}) => {
  if (dynamicProductAd) return dynamicProductAd;

  const highIntentCookie = getHighIntentCookie();

  if (!highIntentCookie) return undefined;

  if (businessLine !== 'Womens') {
    browserLogger({
      feature: 'high-intent',
      level: 'info',
      message: `Found ${HIGH_INTENT_COOKIE_NAME} cookie but high-intent interstitial was not shown. Business line: ${
        businessLine || 'undefined'
      }`,
      team: 'client-onboarding',
      context: {
        cookieData: highIntentCookie,
        visitorId: getVisitorIdCookie(),
        referrer: window?.document?.referrer || '',
        businessLine,
      },
    });
  }

  return highIntentCookie;
};
